import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'iconify-icon';
import axios from 'axios';
import { toast } from 'sonner';
import { trackFormSubmission } from '../lib/analytics';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BecomeFranchisee = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', phone: '', city: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const benefits = [
    {
      icon: 'solar:chart-linear',
      title: 'Mercado em Crescimento',
      description: 'O Eneagrama está em alta e a demanda por desenvolvimento pessoal e corporativo só cresce'
    },
    {
      icon: 'solar:diploma-verified-linear',
      title: 'Marca Consolidada',
      description: '26 anos de história, 1.529 empresas atendidas e reconhecimento nacional'
    },
    {
      icon: 'solar:book-linear',
      title: 'Metodologia Completa',
      description: 'Acesso a 4 programas exclusivos: E-Personalidade, E-Corpo, E-Processual e E-Vendas'
    },
    {
      icon: 'solar:users-group-rounded-linear',
      title: 'Suporte Contínuo',
      description: 'Treinamento completo, marketing, gestão e suporte operacional'
    },
    {
      icon: 'solar:star-linear',
      title: 'Certificação Internacional',
      description: 'Programas certificados e reconhecidos seguindo padrões internacionais'
    },
    {
      icon: 'solar:graph-up-linear',
      title: 'ROI Atrativo',
      description: 'Modelo de negócio escalável com múltiplas fontes de receita'
    }
  ];

  const steps = [
    { number: '01', title: 'Contato Inicial', description: 'Preencha o formulário e nossa equipe entrará em contato' },
    { number: '02', title: 'Apresentação', description: 'Conheça o modelo de negócio e tire todas as suas dúvidas' },
    { number: '03', title: 'Análise de Perfil', description: 'Avaliamos se há fit entre você e a franquia' },
    { number: '04', title: 'Treinamento', description: 'Capacitação completa na metodologia e gestão' },
    { number: '05', title: 'Inauguração', description: 'Abertura da sua unidade com todo suporte necessário' }
  ];

  return (
    <div>
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-24 border-b border-neutral-900 relative overflow-hidden">
          <div className="absolute inset-0 bg-grid opacity-30"></div>
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span className="text-xs font-mono uppercase tracking-[0.3em] text-gray-400 mb-4 block animate-on-scroll">
                {t('pages.become_franchisee.hero_label')}
              </span>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 animate-on-scroll delay-100">
                {t('pages.become_franchisee.title_part1')} <span className="text-accent-orange">{t('pages.become_franchisee.title_highlight')}</span>
              </h1>
              <p className="text-lg text-neutral-400 mb-8 leading-relaxed animate-on-scroll delay-200">
                {t('pages.become_franchisee.hero_subtitle')}
              </p>
              <div className="flex flex-wrap justify-center gap-4 animate-on-scroll delay-300">
                <a href="#formulario" className="beam-btn group">
                  <div className="beam-spinner"></div>
                  <div className="beam-btn-content">
                    <span>{t('pages.become_franchisee.i_want_to_be')}</span>
                    <iconify-icon icon="solar:arrow-right-linear"></iconify-icon>
                  </div>
                </a>
                <a href="#beneficios" className="px-8 py-4 text-sm font-mono uppercase tracking-wider text-neutral-400 hover:text-[#EAEAEA] transition-colors">
                  {t('pages.become_franchisee.learn_more')}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 relative" style={{ background: "rgba(10, 10, 10, 0.72)" }}>
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 animate-on-scroll">
              {[
                { value: '20+', key: 'about.stats.years' },
                { value: '36', key: 'about.stats.units' },
                { value: '3.000+', key: 'about.stats.companies' },
                { value: '70.000+', key: 'about.stats.people' }
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl md:text-5xl font-bold mb-2">{stat.value}</div>
                  <div className="text-xs font-mono uppercase tracking-widest text-gray-400">{t(stat.key)}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section id="beneficios" className="py-24 bg-[#111111] border-t border-neutral-800">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-4 animate-on-scroll">{t('pages.become_franchisee.benefits_title')}</h2>
              <p className="text-lg text-neutral-400 animate-on-scroll delay-100">{t('pages.become_franchisee.benefits_subtitle')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="glow-card bg-[#1A1A1A] border border-[#333333] p-8 rounded-xl hover-lift animate-on-scroll"
                  style={{ transitionDelay: `${index * 0.1}s` }}
                >
                  <div className="w-14 h-14 rounded-full bg-[#1F1F1F] flex items-center justify-center mb-4">
                    <iconify-icon icon={benefit.icon} className="text-3xl"></iconify-icon>
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                  <p className="text-neutral-400">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-24 relative" style={{ background: "rgba(10, 10, 10, 0.72)" }}>
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-4 animate-on-scroll">{t('pages.become_franchisee.process_title')}</h2>
              <p className="text-lg text-neutral-400 animate-on-scroll delay-100">{t('pages.become_franchisee.process_subtitle')}</p>
            </div>
            <div className="space-y-6">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className="glow-card bg-[#111111] p-8 rounded-xl flex flex-col md:flex-row items-start gap-6 animate-on-scroll"
                  style={{ transitionDelay: `${index * 0.1}s` }}
                >
                  <div className="text-6xl font-bold text-neutral-200">{step.number}</div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-semibold mb-2">{step.title}</h3>
                    <p className="text-neutral-400">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Form Section */}
        <section id="formulario" className="relative py-24 text-white" style={{ background: 'rgba(10, 10, 10, 0.78)' }}>
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-5xl font-bold mb-4 text-white">{t('pages.become_franchisee.form_title')}</h2>
                <p className="text-lg text-white/80">{t('pages.become_franchisee.form_subtitle')}</p>
              </div>
              <form className="space-y-4" onSubmit={async (e) => {
                e.preventDefault();
                setSubmitting(true);
                try {
                  await axios.post(`${API_URL}/public/franchisee-application`, form);
                  trackFormSubmission('seja_franqueado');
                  toast.success(t('franchisee.success'));
                  setForm({ name: '', email: '', phone: '', city: '', message: '' });
                } catch (err) {
                  toast.error(err.response?.data?.detail || t('contact.error'));
                } finally {
                  setSubmitting(false);
                }
              }} data-testid="franchisee-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t('contact.field_name')}
                    data-testid="franchisee-name-input"
                    className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/50 outline-none focus:border-white/40"
                  />
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder={t('contact.field_email')}
                    data-testid="franchisee-email-input"
                    className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/50 outline-none focus:border-white/40"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder={t('contact.field_phone')}
                    data-testid="franchisee-phone-input"
                    className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/50 outline-none focus:border-white/40"
                  />
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder={t('franchisee.field_city')}
                    data-testid="franchisee-city-input"
                    className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/50 outline-none focus:border-white/40"
                  />
                </div>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder={t('franchisee.field_message')}
                  rows="4"
                  data-testid="franchisee-message-input"
                  className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/50 outline-none focus:border-white/40 resize-none"
                ></textarea>
                <Button type="submit" disabled={submitting} data-testid="franchisee-submit-btn" className="w-full bg-accent-orange text-white hover:bg-[#E55D00] h-12 text-sm font-bold uppercase tracking-wider">
                  {submitting ? t('common.sending') : t('franchisee.submit')}
                </Button>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BecomeFranchisee;
